import useIsMobile from '../hooks/useIsMobile';
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import appointmentService from '../services/appointmentService';
import PageHeader from '../components/ui/PageHeader';
import EmptyState from '../components/ui/EmptyState';
import { useAuth } from '../context/AuthContext';
import CaseFileStatusBadge from '../components/ui/StatusBadge';

// ── Helpers ────────────────────────────────────────────────────────────────────

const activityStatusConfig = {
  pending:     { label: 'Pendiente',   color: '#F59E0B', bg: '#FEF3C7' },
  in_progress: { label: 'En progreso', color: '#3B82F6', bg: '#DBEAFE' },
  completed:   { label: 'Completado',  color: '#10B981', bg: '#D1FAE5' },
};

const appointmentStatusColors = {
  programada: '#3b82f6', confirmada: '#10b981',
  realizada: '#6b7280', cancelada: '#ef4444', reprogramada: '#f59e0b',
};

const StatusBadge = ({ config }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center',
    padding: '3px 9px', borderRadius: 20,
    fontSize: 11, fontWeight: 600,
    backgroundColor: config?.bg || '#F3F4F6',
    color: config?.color || '#374151',
    whiteSpace: 'nowrap',
  }}>
    {config?.label || '—'}
  </span>
);

// ── Stat Card — clickeable ─────────────────────────────────────────────────────

const StatCard = ({ value, label, iconPath, iconColor, iconBg, highlight, highlightColor, to }) => {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => to && navigate(to)}
      style={{
        background: '#fff',
        borderRadius: 12,
        border: highlight ? `1.5px solid ${highlightColor || iconColor}` : '1px solid #E5E7EB',
        boxShadow: highlight ? `0 4px 14px ${highlightColor || iconColor}22` : '0 1px 4px rgba(0,0,0,0.06)',
        padding: '18px 20px',
        display: 'flex', alignItems: 'center', gap: 16,
        transition: 'box-shadow 0.2s, transform 0.2s',
        cursor: to ? 'pointer' : 'default',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 18px rgba(0,0,0,0.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = highlight ? `0 4px 14px ${iconColor}22` : '0 1px 4px rgba(0,0,0,0.06)'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div style={{ width: 48, height: 48, borderRadius: 12, backgroundColor: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg style={{ width: 22, height: 22, color: iconColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d={iconPath} />
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 28, fontWeight: 800, color: highlight ? (highlightColor || iconColor) : '#111827', lineHeight: 1, letterSpacing: '-1px' }}>
          {value}
        </div>
        <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4, fontWeight: 500 }}>{label}</div>
      </div>
      {to && <svg style={{ width: 14, height: 14, color: '#D1D5DB', flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>}
    </div>
  );
};

// ── Section Card ───────────────────────────────────────────────────────────────

const SectionCard = ({ title, action, children, noPadding = false }) => (
  <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #F3F4F6' }}>
      <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{title}</span>
      {action}
    </div>
    <div style={noPadding ? {} : { padding: '16px 18px' }}>
      {children}
    </div>
  </div>
);

// ── Timestamp helper ───────────────────────────────────────────────────────────

function timeAgo(date) {
  const mins = Math.floor((Date.now() - date.getTime()) / 60000);
  if (mins < 1) return 'ahora mismo';
  if (mins === 1) return 'hace 1 min';
  if (mins < 60) return `hace ${mins} min`;
  return `hace ${Math.floor(mins / 60)}h`;
}

// ── Main ───────────────────────────────────────────────────────────────────────

const Dashboard = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [arAlerts, setArAlerts] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const isExecutive = user?.role === 'notary' || user?.role === 'supervisor';

  const fetchAll = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const [dashRes, arRes, apptRes] = await Promise.all([
        api.get('/dashboard'),
        api.get('/accounts-receivable/alerts').catch(() => ({ data: null })),
        appointmentService.getToday().catch(() => ({ data: [] })),
      ]);
      setData(dashRes.data);
      setArAlerts(arRes.data);
      setTodayAppointments(apptRes.data ?? []);
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchAll(refreshKey > 0); }, [fetchAll, refreshKey]); // eslint-disable-line

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', flexDirection: 'column', gap: 14 }}>
      <div style={{ width: 36, height: 36, border: '3px solid #EEF0FB', borderTopColor: '#4F63D2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      <span style={{ fontSize: 13, color: '#9CA3AF' }}>Cargando dashboard...</span>
    </div>
  );

  const { stats, oldest_case_files, pending_docs, recent_activities, user_stats } = data;
  const totalCaseFiles = Object.values(stats.by_stage_type).reduce((a, b) => a + b, 0) || 1;

  const statCards = [
    {
      value: stats.active_case_files,
      label: 'Expedientes activos',
      iconColor: '#4F63D2', iconBg: '#EEF0FB',
      to: '/case-files',
      iconPath: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z',
    },
    {
      value: stats.stale_case_files,
      label: `Sin actividad (+${stats.stale_days}d)`,
      iconColor: '#F59E0B', iconBg: '#FEF3C7',
      highlight: stats.stale_case_files > 0,
      to: '/case-files?stale=1',
      iconPath: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
    },
    {
      value: stats.completed_case_files,
      label: 'Completados',
      iconColor: '#10B981', iconBg: '#D1FAE5',
      to: '/case-files?stage_type=completed',
      iconPath: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    },
    {
      value: stats.new_clients_month,
      label: 'Clientes este mes',
      iconColor: '#7C3AED', iconBg: '#EDE9FE',
      to: '/clients',
      iconPath: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
    },
  ];

  const roleLabels = { notary: 'Notario', supervisor: 'Supervisor', staff: 'Staff' };
  const roleColors = { notary: { bg: '#EEF0FB', color: '#4F63D2' }, supervisor: { bg: '#DBEAFE', color: '#1D4ED8' }, staff: { bg: '#F3F4F6', color: '#374151' } };

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle={
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span>{`Hoy es ${new Date().toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`}</span>
            {lastUpdated && (
              <span style={{ fontSize: 11, color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: 4 }}>
                · Actualizado {timeAgo(lastUpdated)}
              </span>
            )}
          </span>
        }
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Ir al Dashboard Ejecutivo — solo notario/supervisor */}
            {isExecutive && (
              <Link to="/notary-dashboard"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, textDecoration: 'none', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
                📈 Dashboard Ejecutivo
              </Link>
            )}
            {/* Botón refresh */}
            <button
              onClick={() => setRefreshKey(k => k + 1)}
              disabled={refreshing}
              title="Actualizar datos"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, cursor: refreshing ? 'not-allowed' : 'pointer', opacity: refreshing ? 0.6 : 1, transition: 'background 0.15s' }}
              onMouseEnter={e => { if (!refreshing) e.currentTarget.style.backgroundColor = '#F9FAFB'; }}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
              <svg style={{ width: 14, height: 14, animation: refreshing ? 'spin 0.7s linear infinite' : 'none' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {refreshing ? 'Actualizando...' : 'Actualizar'}
            </button>
          </div>
        }
      />

      {/* ── Stat Cards ─────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 22 }}>
        {statCards.map((card, i) => <StatCard key={i} {...card} />)}
      </div>

      {/* ── Alertas ────────────────────────────────────────── */}
      {stats.stale_case_files > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, padding: '12px 16px', borderRadius: 10, backgroundColor: '#FEF3C7', border: '1px solid #FDE68A', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>⚠️</span>
            <div>
              <span style={{ fontWeight: 600, fontSize: 13, color: '#92400E' }}>
                {stats.stale_case_files} expediente{stats.stale_case_files > 1 ? 's' : ''} sin actividad
              </span>
              <span style={{ fontSize: 13, color: '#92400E', marginLeft: 6 }}>
                — sin cambios por más de {stats.stale_days} días
              </span>
            </div>
          </div>
          <Link to="/case-files?stale=1" style={{ padding: '6px 14px', borderRadius: 7, backgroundColor: '#F59E0B', color: '#fff', fontWeight: 600, fontSize: 12, textDecoration: 'none', whiteSpace: 'nowrap' }}>
            Ver expedientes
          </Link>
        </div>
      )}

      {arAlerts?.pending_count > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
          padding: '12px 16px', borderRadius: 10, marginBottom: 16,
          backgroundColor: arAlerts.overdue_count > 0 ? '#FEE2E2' : '#FEF3C7',
          border: `1px solid ${arAlerts.overdue_count > 0 ? '#FECACA' : '#FDE68A'}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>{arAlerts.overdue_count > 0 ? '🔴' : '🟡'}</span>
            <div>
              <span style={{ fontWeight: 600, fontSize: 13, color: arAlerts.overdue_count > 0 ? '#991B1B' : '#92400E' }}>
                {arAlerts.overdue_count > 0
                  ? `${arAlerts.overdue_count} vencido${arAlerts.overdue_count !== 1 ? 's' : ''}`
                  : 'Sin vencidos'}
                {arAlerts.pending_count > arAlerts.overdue_count
                  ? ` · ${arAlerts.pending_count - (arAlerts.overdue_count || 0)} pendiente${arAlerts.pending_count - (arAlerts.overdue_count || 0) !== 1 ? 's' : ''} sin vencer`
                  : ''}
              </span>
              <span style={{ fontSize: 13, color: arAlerts.overdue_count > 0 ? '#991B1B' : '#92400E', marginLeft: 6 }}>
                — Total: <strong>${Number(arAlerts.total_balance).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</strong>
              </span>
            </div>
          </div>
          <Link to="/accounts-receivable" style={{ padding: '6px 14px', borderRadius: 7, backgroundColor: arAlerts.overdue_count > 0 ? '#EF4444' : '#F59E0B', color: '#fff', fontWeight: 600, fontSize: 12, textDecoration: 'none', whiteSpace: 'nowrap' }}>
            Ver cuentas
          </Link>
        </div>
      )}

      {/* ── Fila 1: Estado + Expedientes viejos ────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', gap: 16, marginBottom: 16 }}>

        {/* Estado de expedientes — barras clickeables */}
        <SectionCard title="Estado de expedientes">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              { key: 'active',    label: 'Activos',     color: '#3B82F6', stageType: 'active' },
              { key: 'completed', label: 'Completados', color: '#10B981', stageType: 'completed' },
              { key: 'cancelled', label: 'Cancelados',  color: '#EF4444', stageType: 'cancelled' },
            ].map(({ key, label, color, stageType }) => {
              const count = stats.by_stage_type[key] || 0;
              const pct   = Math.round((count / totalCaseFiles) * 100);
              return (
                <div
                  key={key}
                  onClick={() => navigate(`/case-files?stage_type=${stageType}`)}
                  title={`Ver expedientes ${label.toLowerCase()}`}
                  style={{ cursor: 'pointer', borderRadius: 8, padding: '6px 8px', margin: '-6px -8px', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                    <span style={{ fontSize: 12, color: '#4B5563', fontWeight: 500 }}>{label}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{count} <span style={{ color: '#9CA3AF', fontWeight: 400 }}>({pct}%)</span></span>
                  </div>
                  <div style={{ height: 7, backgroundColor: '#F3F4F6', borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, backgroundColor: color, borderRadius: 99, transition: 'width 0.6s ease' }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#9CA3AF' }}>Nuevos este mes</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#4F63D2', background: '#EEF0FB', padding: '2px 10px', borderRadius: 20 }}>{stats.new_case_files_month}</span>
          </div>
        </SectionCard>

        {/* Expedientes más viejos — filas clickeables */}
        <SectionCard
          title="Expedientes con más tiempo abiertos"
          noPadding
          action={<Link to="/case-files" style={{ fontSize: 12, fontWeight: 600, color: '#4F63D2', textDecoration: 'none', padding: '4px 10px', borderRadius: 6, background: '#EEF0FB' }}>Ver todos</Link>}
        >
          {oldest_case_files.length === 0 ? (
            <EmptyState icon="📂" title="No hay expedientes activos" />
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#F9FAFB' }}>
                  {['', 'Cliente / Expediente', 'Responsable', 'Docs', 'Tiempo', 'Estado'].map((h, i) => (
                    <th key={i} className={i >= 2 && i <= 3 ? 'mobile-hide' : ''} style={{ padding: '9px 14px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9CA3AF', textAlign: 'left', borderBottom: '1px solid #F3F4F6', whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {oldest_case_files.map(cf => {
                  const docStats  = cf.document_stats;
                  const docsOk    = docStats?.total > 0 && docStats?.delivered === docStats?.total;
                  const docsLabel = docStats?.total > 0 ? `${docStats.delivered}/${docStats.total}` : '—';
                  return (
                    <tr key={cf.id}
                      onClick={() => navigate(`/case-files/${cf.id}`)}
                      style={{ backgroundColor: cf.is_stale ? '#FFFBF0' : '#fff', cursor: 'pointer', transition: 'background 0.1s' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FAFBFF'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = cf.is_stale ? '#FFFBF0' : '#fff'}>
                      <td style={{ padding: '10px 14px', width: 28 }}>
                        {cf.is_stale && <span title="Sin actividad reciente" style={{ fontSize: 14 }}>⚠️</span>}
                      </td>
                      <td style={{ padding: '10px 14px' }} onClick={e => e.stopPropagation()}>
                        <Link to={`/clients/${cf.client_id}`} style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#4F63D2', textDecoration: 'none', marginBottom: 1 }}>
                          {cf.client?.name}
                        </Link>
                        <Link to={`/case-files/${cf.id}`} style={{ fontSize: 12, color: '#6B7280', textDecoration: 'none' }}>
                          {cf.title}
                        </Link>
                      </td>
                      <td className="mobile-hide" style={{ padding: '10px 14px', fontSize: 12, color: '#6B7280' }}>{cf.assigned_user?.name || <span style={{ color: '#EF4444', fontWeight: 600 }}>Sin asignar</span>}</td>
                      <td className="mobile-hide" style={{ padding: '10px 14px' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, backgroundColor: docsOk ? '#D1FAE5' : docStats?.total > 0 ? '#FEF3C7' : '#F3F4F6', color: docsOk ? '#065F46' : docStats?.total > 0 ? '#92400E' : '#9CA3AF' }}>
                          {docsLabel}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, backgroundColor: cf.is_stale ? '#FEE2E2' : '#F3F4F6', color: cf.is_stale ? '#991B1B' : '#374151' }}>
                          {cf.elapsed_time}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <CaseFileStatusBadge status={cf.status} stageType={cf.stage_type} order={cf.procedure_type_status?.order} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </SectionCard>
      </div>

      {/* ── Carga de trabajo — filas clickeables ───────────── */}
      {user_stats?.length > 0 && (
        <SectionCard title="Carga de trabajo por usuario" noPadding style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#F9FAFB' }}>
                  {['Usuario', 'Rol', 'Activos', 'Sin act.', 'Completados', 'Carga'].map((h, i) => (
                    <th key={i} className={[1, 4, 5].includes(i) ? 'mobile-hide' : ''} style={{ padding: '9px 14px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9CA3AF', textAlign: 'left', borderBottom: '1px solid #F3F4F6' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {user_stats.map(u => {
                  const total = (u.active_case_files + u.completed_case_files) || 1;
                  const pct   = Math.round((u.active_case_files / total) * 100);
                  const rc    = roleColors[u.role] || roleColors.staff;
                  return (
                    <tr key={u.id}
                      onClick={() => navigate(`/case-files?assigned_user_id=${u.id}`)}
                      style={{ backgroundColor: '#fff', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FAFBFF'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
                      <td style={{ padding: '10px 14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                          <div style={{ width: 30, height: 30, borderRadius: '50%', backgroundColor: '#EEF0FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#4F63D2', fontSize: 12, flexShrink: 0 }}>
                            {u.name.charAt(0).toUpperCase()}
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{u.name}</span>
                        </div>
                      </td>
                      <td className="mobile-hide" style={{ padding: '10px 14px' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20, backgroundColor: rc.bg, color: rc.color }}>{roleLabels[u.role]}</span>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#4F63D2' }}>{u.active_case_files}</span>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        {u.stale_case_files > 0
                          ? <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20, backgroundColor: '#FEE2E2', color: '#991B1B' }}>⚠️ {u.stale_case_files}</span>
                          : <span style={{ fontSize: 13, color: '#D1D5DB' }}>—</span>}
                      </td>
                      <td className="mobile-hide" style={{ padding: '10px 14px' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#10B981' }}>{u.completed_case_files}</span>
                      </td>
                      <td className="mobile-hide" style={{ padding: '10px 14px', minWidth: 160 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 6, backgroundColor: '#F3F4F6', borderRadius: 99, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, backgroundColor: u.stale_case_files > 0 ? '#EF4444' : '#4F63D2', borderRadius: 99 }} />
                          </div>
                          <span style={{ fontSize: 11, color: '#9CA3AF', minWidth: 24, textAlign: 'right' }}>{pct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}

      {/* ── Fila 3: Pendientes + Actividades recientes ─────── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', gap: 16, marginBottom: 16 }}>

        {/* Documentos pendientes — descripción enriquecida */}
        <SectionCard title="Documentos obligatorios pendientes" noPadding>
          {pending_docs.length === 0 ? (
            <EmptyState icon="✅" title="Todo al día" subtitle="No hay documentos obligatorios pendientes" />
          ) : (
            <div>
              {pending_docs.map((doc, i) => (
                <div
                  key={i}
                  onClick={() => navigate(`/case-files/${doc.case_file_id}`)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 18px', borderBottom: i < pending_docs.length - 1 ? '1px solid #F3F4F6' : 'none', cursor: 'pointer', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FAFBFF'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                  <div style={{ minWidth: 0, marginRight: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#111827', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {doc.case_file_title}
                    </span>
                    <span style={{ fontSize: 12, color: '#9CA3AF' }}>{doc.client_name}</span>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20, backgroundColor: '#FEE2E2', color: '#991B1B', display: 'block' }}>
                      {doc.pending_count} doc{doc.pending_count !== 1 ? 's' : ''}
                    </span>
                    <span style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2, display: 'block' }}>obligatorio{doc.pending_count !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* Actividades recientes — filas clickeables + acción "Ver expedientes" */}
        <SectionCard
          title="Actividades recientes"
          noPadding
          action={
            <Link to="/case-files" style={{ fontSize: 12, fontWeight: 600, color: '#4F63D2', textDecoration: 'none', padding: '4px 10px', borderRadius: 6, background: '#EEF0FB' }}>
              Ver expedientes
            </Link>
          }
        >
          {recent_activities.length === 0 ? (
            <EmptyState icon="📋" title="No hay actividades recientes" />
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#F9FAFB' }}>
                  {['Actividad', 'Expediente', 'Responsable', 'Hace', 'Estado'].map((h, i) => (
                    <th key={i} className={[2, 3].includes(i) ? 'mobile-hide' : ''} style={{ padding: '9px 14px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9CA3AF', textAlign: 'left', borderBottom: '1px solid #F3F4F6' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recent_activities.map(activity => (
                  <tr key={activity.id}
                    onClick={() => navigate(`/case-files/${activity.case_file_id}`)}
                    style={{ backgroundColor: '#fff', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FAFBFF'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 2 }}>{activity.title}</div>
                      {activity.activity_type?.name && (
                        <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 20, backgroundColor: '#F3F4F6', color: '#6B7280', fontWeight: 500 }}>
                          {activity.activity_type.name}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 14px' }} onClick={e => e.stopPropagation()}>
                      <Link to={`/case-files/${activity.case_file_id}`} style={{ fontSize: 13, color: '#4F63D2', textDecoration: 'none', fontWeight: 500, display: 'block' }}>
                        {activity.case_file?.title}
                      </Link>
                      <span style={{ fontSize: 12, color: '#9CA3AF' }}>{activity.case_file?.client?.name}</span>
                    </td>
                    <td className="mobile-hide" style={{ padding: '10px 14px', fontSize: 12, color: '#6B7280' }}>{activity.user?.name}</td>
                    <td className="mobile-hide" style={{ padding: '10px 14px', fontSize: 12, color: '#9CA3AF', whiteSpace: 'nowrap' }}>{activity.elapsed_time}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <StatusBadge config={activityStatusConfig[activity.status]} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SectionCard>
      </div>

      {/* ── Citas de hoy — con contador ────────────────────── */}
      <SectionCard
        title={`📅 Citas de hoy${todayAppointments.length > 0 ? ` (${todayAppointments.length})` : ''}`}
        noPadding
        action={<Link to="/agenda" style={{ fontSize: 12, fontWeight: 600, color: '#4F63D2', textDecoration: 'none', padding: '4px 10px', borderRadius: 6, background: '#EEF0FB' }}>Ver agenda</Link>}
      >
        {todayAppointments.length === 0 ? (
          <EmptyState icon="📅" title="Sin citas hoy" subtitle="No hay citas programadas para hoy" />
        ) : (
          <div>
            {todayAppointments.map(a => {
              const start = new Date(a.start_datetime);
              const color = appointmentStatusColors[a.status] || '#9CA3AF';
              return (
                <div key={a.id}
                  onClick={() => navigate(`/agenda/${a.id}`)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderBottom: '1px solid #F3F4F6', gap: 12, cursor: 'pointer', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FAFBFF'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                    <div style={{ width: 4, height: 36, borderRadius: 99, backgroundColor: color, flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{a.title}</div>
                      <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 1 }}>
                        {start.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                        {a.client ? ` · ${a.client.name}` : ''}
                        {a.user ? ` · ${a.user.name}` : ''}
                      </div>
                    </div>
                  </div>
                  <svg style={{ width: 14, height: 14, color: '#D1D5DB', flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>
    </div>
  );
};

export default Dashboard;
